import { Link } from 'react-router-dom';
import { money, formatDay } from '../lib/money';
import { coverersOf, myUnit, sharesFor, tripFacts } from '../lib/split';
import { Empty, Glyph, useUnitLookup } from '../components/ui';
import { Icon } from '../components/Icons';
import type { Expense, Settlement, TripData } from '../types';

type FeedItem =
  | { kind: 'expense'; date: string; expense: Expense }
  | { kind: 'settlement'; date: string; settlement: Settlement };

export default function Expenses({ data, uid }: { data: TripData; uid: string }) {
  const { group, units, expenses, settlements } = data;
  const look = useUnitLookup(units);
  const mine = myUnit(units, uid);
  const facts = tripFacts(data);

  if (!expenses.length && !settlements.length) {
    return (
      <Empty title="Nothing spent yet">
        Add the first expense and Evenhouse keeps the running split from there.
      </Empty>
    );
  }

  const items: FeedItem[] = [
    ...expenses.map((e) => ({ kind: 'expense' as const, date: e.date, expense: e })),
    ...settlements.map((s) => ({ kind: 'settlement' as const, date: s.date, settlement: s }))
  ].sort((a, b) => b.date.localeCompare(a.date));

  const days: Record<string, FeedItem[]> = {};
  for (const it of items) (days[it.date] ||= []).push(it);

  return (
    <>
      <div className="card" style={{
        padding: '13px 15px', marginTop: 2, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', gap: 12
      }}>
        <div>
          <div className="eyebrow">Trip total</div>
          <div className="amt" style={{ fontSize: 19, fontWeight: 700, marginTop: 2 }}>
            {money(facts.totalMinor, group.currency)}
          </div>
        </div>
        {mine && (
          <div style={{ textAlign: 'right' }}>
            <div className="eyebrow">Your family's share</div>
            <div className="amt" style={{ fontSize: 19, fontWeight: 700, marginTop: 2 }}>
              {money(facts.share[mine.id] ?? 0, group.currency)}
            </div>
          </div>
        )}
      </div>

      {Object.keys(days).sort().reverse().map((date) => (
        <div key={date}>
          <div className="day">{formatDay(date)}</div>
          <div className="list">
            {days[date].map((it) =>
              it.kind === 'expense' ? (
                <ExpenseRow
                  key={it.expense.id}
                  data={data}
                  expense={it.expense}
                  mineId={mine?.id}
                  payer={look.name(it.expense.paidByUnitId)}
                  isMine={Boolean(mine && it.expense.paidByUnitId === mine.id)}
                />
              ) : (
                <div className="row" key={it.settlement.id}>
                  <Glyph name="settle" tint />
                  <span className="main">
                    <span className="t">
                      {look.name(it.settlement.fromUnitId)} → {look.name(it.settlement.toUnitId)}
                    </span>
                    <span className="s">
                      Payment recorded{it.settlement.method ? ` · ${it.settlement.method}` : ''}
                    </span>
                  </span>
                  <span className="end">
                    <span className="t amt pos">{money(it.settlement.amountMinor, group.currency)}</span>
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      ))}
    </>
  );
}

function ExpenseRow({ data, expense, mineId, payer, isMine }: {
  data: TripData;
  expense: Expense;
  mineId?: string;
  payer: string;
  isMine: boolean;
}) {
  const myShare = mineId ? sharesFor(data.units, expense)[mineId] ?? 0 : 0;
  const method = expense.basis === 'perUnit' ? 'per family'
    : expense.basis === 'custom' ? 'custom' : 'per person';
  const payers = [isMine ? 'You' : payer, ...coverersOf(data.units, expense).map((u) => u.name)];

  return (
    <Link className="row" to={`/t/${data.group.id}/e/${expense.id}`}>
      <Glyph name={expense.category} />
      <span className="main">
        <span className="t">{expense.desc}</span>
        <span className="s">{payers.join(' + ')} paid · {method}</span>
      </span>
      <span className="end">
        <span className="t amt">{money(expense.amountMinor, data.group.currency)}</span>
        <span className="s amt">
          {mineId
            ? myShare ? `your share ${money(myShare, data.group.currency)}` : 'not in'
            : ''}
        </span>
      </span>
      <span className="chev" style={{ color: 'var(--ink-3)', opacity: .5, display: 'flex' }}>
        <Icon name="chevron" />
      </span>
    </Link>
  );
}
